import {type Address, type CountryOption, type Customer, type StateOption} from "./AddressManagement";
import {type FormEvent, type ReactElement, useEffect, useRef, useState} from "react";
import {toast} from "react-toastify";

interface AddAddressFormProps {
    isOpen: boolean,
    onClose: () => void,
    customer: Customer,
    countries: CountryOption,
    states: StateOption,
    onAddressAdded: (address: Address) => void,
    onAddressUpdated?: (address: Address) => void,
    editAddress?: Address | null,
    editAddressId?: string | null,
    canAddAddress?: boolean,
    canEditAddress?: boolean
}

interface AddressComponent {
    long_name: string;
    short_name: string;
    types: string[];
}

interface Location {
    lat: number;
    lng: number;
}

interface Viewport {
    south: number;
    west: number;
    north: number;
    east: number;
}

interface Geometry {
    location: Location;
    viewport: Viewport;
}

interface PlaceDetails {
    address_components: AddressComponent[];
    adr_address: string;
    formatted_address: string;
    geometry: Geometry;
    icon: string;
    icon_background_color: string;
    icon_mask_base_uri: string;
    name: string;
    place_id: string;
    reference: string;
    types: string[];
    url: string;
    utc_offset: number;
    vicinity: string;
    html_attributions: any[]; // could be string[] or any array, depending on usage
    utc_offset_minutes: number;
}

type FormValues = Record<string, string>;
type SubmitError = Record<string, string>;

const AddAddressForm : React.FC<AddAddressFormProps> = ({
    isOpen,
    onClose,
    customer,
    countries,
    states,
    onAddressAdded,
    onAddressUpdated,
    editAddress,
    editAddressId,
    canAddAddress = true,
    canEditAddress = true,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [submitMessage, setSubmitMessage] = useState<string | null>(null);
    const [formValues, setFormValues] = useState<FormValues>({
        'firstname': customer.firstname ?? '',
        'lastname': customer.lastname ?? '',
        'email': customer.email ?? '',
        'phone': customer.phone ?? '',
        'company': customer.company ?? '',
        'region': '',
        'city': '',
        'country': '',
    });
    const inputRef = useRef<HTMLInputElement>(null);
    const [submitErrors, setSubmitErrors] = useState<SubmitError[]>([]);
    const isEditMode = Boolean(editAddressId || editAddress);

    useEffect(() => {
        if (!isOpen) {
            setIsLoading(false);
            setSubmitErrors([]);
            setSubmitMessage(null);
            setFormValues({
                'firstname': customer.firstname ?? '',
                'lastname': customer.lastname ?? '',
                'email': customer.email ?? '',
                'phone': customer.phone ?? '',
                'company': customer.company ?? '',
                'address1': '',
                'address2': '',
                'postcode': '',
                'city': '',
                'region': '',
                'country': '',
            });
        }
    }, [isOpen, customer]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isOpen]);

    // Separate effect to handle editAddress population
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (!isEditMode) {
            setFormValues({
                'firstname': customer.firstname ?? '',
                'lastname': customer.lastname ?? '',
                'email': customer.email ?? '',
                'phone': customer.phone ?? '',
                'company': customer.company ?? '',
                'address1': '',
                'address2': '',
                'postcode': '',
                'city': '',
                'region': '',
                'country': '',
            });
        }

        if (editAddressId) {
            updateFormValue('id', editAddressId);
        }

        if (editAddress) {
            Object.keys(editAddress).forEach((key) => {
                // @ts-ignore
                updateFormValue(key, editAddress[key])
            })
        }
    }, [editAddress, editAddressId, isEditMode, isOpen, customer]);

    // Effect to handle Google Places autocomplete
    useEffect(() => {
        const input = inputRef.current;
        if (!input) return;

        function handleCustomEvent(e: CustomEvent) {
            const detail = e.detail as PlaceDetails

            let address1 = ''
            let city = '';

            //Get the appropriate fields from the Google response
            detail.address_components.forEach(component => {
                if (component.types.includes('street_number')) {
                    address1 += component.long_name
                } else if (component.types.includes('route')) {
                    address1 += ' ' + component.long_name
                } else if (component.types.includes('locality')|| component.types.includes('postal_town')){
                    city += component.long_name
                } else if (component.types.includes('administrative_area_level_1')) {
                    updateFormValue('state', component.long_name)
                    updateFormValue('region', component.short_name)
                }
                else if (component.types.includes('country')) {
                    updateFormValue('country', component.short_name)
                    updateFormValue('country_name', component.long_name)
                }
                else if (component.types.includes('postal_code')) {
                    updateFormValue('postcode', component.long_name)
                }
            })

            updateFormValue('address1', address1)
            updateFormValue('city', city)
        }

        // @ts-ignore
        input.addEventListener('place_changed', handleCustomEvent);

        // Cleanup on unmount
        return () => {
            // @ts-ignore
            input.removeEventListener('place_changed', handleCustomEvent);
        };
    }, []);

    const updateFormValue = (key: string, value: string) => {
        setFormValues(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    const getStatesForCountry = (stateCode: string) => {
        return states[stateCode as keyof typeof states]
    }

    const onSaveAddressSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!isEditMode && !canAddAddress) {
            setSubmitMessage('Adding addresses is disabled for this page.');
            toast('Adding addresses is disabled for this page.', { type: 'error' });
            return;
        }
        if (isEditMode && !canEditAddress) {
            setSubmitMessage('Editing addresses is disabled for this page.');
            toast('Editing addresses is disabled for this page.', { type: 'error' });
            return;
        }

        setSubmitErrors([])
        setSubmitMessage(null)
        setIsLoading(true)
        try {
            const url = isEditMode ? '/wp-json/weka/v1/address/edit' : '/wp-json/weka/v1/address';
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': wekaApiSettings.nonce
                },
                body: JSON.stringify(formValues)
            });

            const result = await response.json();

            if (!response.ok) {
                if (response.status === 400 && result?.data?.params) {
                    setSubmitErrors(result.data.params);
                    setSubmitMessage(result?.message || 'Please review the highlighted address fields below.');
                } else {
                    setSubmitMessage(result?.message || 'Failed to save address');
                    toast(result?.message || 'Failed to save address', {type: "error"});
                }
                return;
            }

            onClose();

            if (isEditMode) {
                const updatedAddress = result.address ? {
                    ...result.address,
                    default_shipping: result.address.default_shipping != "0",
                    default_billing: result.address.default_billing != "0",
                } as Address : null;

                if (updatedAddress && onAddressUpdated) {
                    onAddressUpdated(updatedAddress);
                }

                toast(result.message || 'Address updated successfully', {type: "success"});
            } else {
                toast('New address added!', {type: "success"})
                let newAddress: Address = result.new_address
                newAddress.default_shipping = result.new_address.default_shipping != "0";
                newAddress.default_billing = result.new_address.default_billing != "0";

                if (onAddressAdded) {
                    onAddressAdded(newAddress)
                }
            }

            console.log('Address saved:', result);

        } catch (err) {
            console.error('Error adding address:', err);
            setSubmitMessage('Something went wrong while saving the address. Please try again.');
            toast('Failed to save address', {type: "error"});
        } finally {
            setIsLoading(false)
        }
    }

    const getSubmitError = (param: string): ReactElement => {
        // @ts-ignore
        let submitError = submitErrors[param];

        if (submitError) {
            return (<p className={'validation-error'}>{submitError}</p>)
        }

        return <></>
    }

    const isSubmitDisabled = isLoading || (isEditMode && !canEditAddress) || (!isEditMode && !canAddAddress);

    if (!isOpen) {
        return null;
    }

    return <div
        className="address-modal-overlay"
        onClick={() => {
            if (!isLoading) {
                onClose();
            }
        }}
    >
        <div
            className="address-modal-dialog"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="address-management-modal-title"
        >
            <div className="address-modal-header">
                <div>
                    <div id="address-management-modal-title" className="address-modal-title">
                        {isEditMode ? 'Edit Address' : 'Add Address'}
                    </div>
                    <p className="address-modal-subtitle">
                        {isEditMode
                            ? 'Update the saved address details below.'
                            : 'Add a new billing or shipping address to your account.'}
                    </p>
                </div>
                <button
                    type="button"
                    aria-label="Close"
                    disabled={isLoading}
                    onClick={onClose}
                    className={`address-modal-close${isLoading ? ' is-disabled' : ''}`}
                >
                    &times;
                </button>
            </div>

            <form
                onSubmit={onSaveAddressSubmit}
                className="secondary-color-input-focus weka-form address-modal-form"
            >
                {isLoading && <div className={'loading-modal address-modal-loading'}>
                    <svg version="1.1" id="loader" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="50px" height="50px" viewBox="0 0 50 50"><circle cx="25" cy="25" r="20" fill="none" stroke="#3498db" strokeWidth="5" strokeLinecap="round" strokeDasharray="31.4 31.4" transform="rotate(0 25 25)"><animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite"/></circle></svg>
                </div>}

                {(submitMessage || Object.keys(submitErrors).length > 0) && (
                    <div className="address-modal-error" role="alert">
                        {submitMessage || 'Please review the highlighted address fields below.'}
                    </div>
                )}

                <div className={'address-modal-grid form-row two-columns'}>
                            <div>
                                <label>First name</label>
                                <input type="text" name="firstname" placeholder="First Name"
                                       className={'input-text'}
                                       onChange={(e) => updateFormValue('firstname', e.target.value)}
                                       value={formValues['firstname']}/>
                                {getSubmitError('firstname')}
                            </div>
                            <div>
                                <label>Last name</label>
                                <input type="text"
                                       name="lastname"
                                       placeholder="Last Name"
                                       className={'input-text'}
                                       onChange={(e) => updateFormValue('lastname', e.target.value)}
                                       value={formValues['lastname']}/>
                                {getSubmitError('lastname')}
                            </div>
                </div>
                <div className={'form-row'}>
                            <label>Email</label>
                            <input type="email"
                                   name="email"
                                   placeholder="Email"
                                   onChange={(e) => updateFormValue('email', e.target.value)}
                                   value={formValues['email']}
                                   className={'input-text'}/>
                            {getSubmitError('email')}
                </div>
                <div className={'address-modal-grid form-row two-columns'}>
                    <div>
                            <label>Phone</label>
                            <input type="text"
                                   name="phone"
                                   placeholder="Phone"
                                   onChange={(e) => updateFormValue('phone', e.target.value)}
                                   value={formValues['phone']} className={'input-text'}/>
                            {getSubmitError('phone')}
                    </div>
                    <div>
                            <label>Company</label>
                            <input type="text" name="company" placeholder="Company"
                                   onChange={(e) => updateFormValue('company', e.target.value)}
                                   value={formValues['company']} className={'input-text'}/>
                            {getSubmitError('company')}
                    </div>
                </div>


                <div className={'form-row'}>
                            <label>Street address</label>
                            <input type="text" name="address1" id="create_address_1" placeholder="House number and street name" value={formValues['address1']} onChange={(e) => updateFormValue('address1', e.target.value)} className={'input-text'}/>
                            {getSubmitError('address1')}
                </div>
                <div className={'form-row'}>
                            <label>Address line 2 (optional)</label>
                            <input type="text" name="address2" id="create_address_2" placeholder="Apartment, suite, unit, etc." value={formValues['address2'] || ''} onChange={(e) => updateFormValue('address2', e.target.value)} className={'input-text'}/>
                            {getSubmitError('address2')}
                </div>
                <div className={'address-modal-grid form-row two-columns'}>
                    <div>
                            <label>Town / City</label>
                            <input type="text" name="city" id="create_address_city" value={formValues['city']} onChange={(e) => updateFormValue('city', e.target.value)} className={'input-text'}/>
                            {getSubmitError('city')}
                    </div>
                    <div>
                        <label>Postcode / ZIP</label>
                        <input type="text" name="postcode" id="create_address_postcode" value={formValues['postcode']} onChange={(e) => updateFormValue('postcode', e.target.value)} className={'input-text'}/>
                        {getSubmitError('postcode')}
                    </div>
                </div>

                <div className={'address-modal-grid form-row two-columns'}>
                    <div>
                        <label>Country</label>
                        {/*@ts-ignore*/}
                        <select value={formValues['country']} name="country" className={'input-text'} onChange={(e) => updateFormValue('country', e.target.value)}>
                            <option value="">Select a country</option>
                            {Object.keys(countries).map((countryCode) => (
                                <option key={countryCode} value={countryCode}>{countries[countryCode]}</option>
                            ))}
                        </select>
                        {getSubmitError('country')}
                    </div>
                    <div>
                        <label>State / Region</label>
                        <select value={formValues['region']} name="region" className={'input-text'} onChange={(e) => updateFormValue('region', e.target.value)}>
                            <option value="">Select a state</option>
                            {(() => {
                                const country = formValues['country'];
                                if (!country) return null;
                                const countryStates = states[country as keyof typeof states];
                                if (!countryStates) return null;
                                return Object.keys(countryStates).map((stateCode) => (
                                    <option key={stateCode} value={stateCode}>{countryStates[stateCode]}</option>
                                ));
                            })()}
                        </select>
                        {getSubmitError('region')}
                    </div>
                </div>

                        {/* <div className={'form-row'}>
                            <label>Address</label>
                            <div>
                                <input ref={inputRef}
                                       className="places-autocomplete-input"
                                       type="text"
                                       data-country={customer.country}
                                       placeholder="Start typing an address..." />
                            </div>
                            {getSubmitError('address1')}
                        </div> */}

                <input type="hidden" name="country2" id="create_address_country" value={formValues['country']} readOnly/>

                <div className="address-modal-actions selector-modal__actions">
                    <button
                        type="submit"
                        disabled={isSubmitDisabled}
                        className={`button weka-primary-btn selector-modal__button selector-modal__button--primary address-modal-button${isSubmitDisabled ? ' is-disabled' : ''}`}
                    >
                        {isLoading ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Address' : 'Add Address')}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        className={`button weka-secondary-btn selector-modal__button selector-modal__button--secondary address-modal-button${isLoading ? ' is-disabled' : ''}`}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    </div>
}

export default AddAddressForm;
